import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table, Button, Modal, Form, Input, message, Image, Tag,
  Dropdown, Space, Tooltip, Drawer, Divider, Switch, DatePicker, Row, Col, Select,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, HeartOutlined,
  SearchOutlined, EyeOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined,
  UserOutlined, PictureOutlined, HeartFilled, FileTextOutlined,
  UploadOutlined, IdcardOutlined, EnvironmentOutlined,
  ShareAltOutlined, CrownOutlined,
  AppstoreOutlined, UnorderedListOutlined,
  TeamOutlined,
  TrophyOutlined, CameraOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  User_SuccessStoriesGet, User_SuccessStoriesPost,
  updateStoryStatus, User_SuccessStoriesUpdate, User_SuccessStoriesRemove, User_StateGet, getDistrictsByState,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import { formatDate, generateRefNumber } from "../../Utils/formatters";
import "./SuccessStories.css";

const { Option } = Select;

function SuccessStories() {
  const [stories, setStories] = useState([]);
  const [filteredStories, setFilteredStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const isFetchingRef = useRef(false);

  const [viewMode, setViewMode] = useState("grid");
  const [statusFilter, setStatusFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [shareStory, setShareStory] = useState(null);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [form] = Form.useForm();

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingStory, setRejectingStory] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [approvalLoading, setApprovalLoading] = useState({});

  const [storyStates, setStoryStates] = useState([]);
  const [storyDistricts, setStoryDistricts] = useState([]);
  const [storyCities, setStoryCities] = useState([]);
  const [storyStateLoading, setStoryStateLoading] = useState(false);
  const [storyDistrictLoading, setStoryDistrictLoading] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchStories = useCallback(async () => {
    if (isFetchingRef.current) return;
    try {
      isFetchingRef.current = true;
      setLoading(true);

      const response = await User_SuccessStoriesGet();
      let rawData = Array.isArray(response) ? response : response?.data || response?.results || [response];

      const mappedStories = rawData.map((item, index) => {
        const imageUrl = item.img_url || item.img || "";

        return {
          id: item.id || index + 1,
          title: item.name || `Story ${index + 1}`,
          story: item.text || "No description",
          image: imageUrl,
          bride_name: item.wife_name || "",
          groom_name: item.husband_name || "",
          city: item.city || "",
          district: item.district || "",
          state: item.state || "",
          marriage_date: item.marriage_date || null,
          created_date: item.created_at || new Date().toISOString(),
          updated_date: item.updated_at || item.created_at,
          status: item.status || 'pending',
          submitted_by_role: item.submitted_by_role || 'admin',
          submitted_by_name: item.submitted_by_name || '',
          submitted_by_id: item.submitted_by_id || item.user_id || '',
          admin_note: item.admin_note || '',
          is_featured: item.is_featured || false,
          is_active: item.is_active !== undefined ? item.is_active : true,
          _raw: item
        };
      });

      setStories(mappedStories);
      setFilteredStories(mappedStories);
    } catch (error) {
      message.error("Failed to fetch stories");
      setStories([]);
      setFilteredStories([]);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  useEffect(() => {
    let filtered = [...stories];
    if (searchText) {
      filtered = filtered.filter(s =>
        s.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        s.bride_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        s.groom_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        generateRefNumber("story", s.id).toLowerCase().includes(searchText.toLowerCase())
      );
    }
    if (statusFilter === "active") filtered = filtered.filter(s => s.is_active !== false);
    else if (statusFilter === "inactive") filtered = filtered.filter(s => s.is_active === false);
    if (featuredFilter) filtered = filtered.filter(s => s.is_featured === true);
    if (approvalFilter !== 'all') filtered = filtered.filter(s => s.status === approvalFilter);
    setFilteredStories(filtered);
  }, [searchText, statusFilter, featuredFilter, approvalFilter, stories]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        setStoryStateLoading(true);
        const res = await User_StateGet();
        setStoryStates(Array.isArray(res) ? res : res?.data || []);
      } catch { setStoryStates([]); }
      finally { setStoryStateLoading(false); }
    };
    loadStates();
  }, []);

  const handleStoryStateChange = async (value) => {
    setStoryDistricts([]);
    form.setFieldsValue({ district: undefined, city: undefined });
    if (!value) return;
    try {
      setStoryDistrictLoading(true);
      const response = await getDistrictsByState(value);
      setStoryDistricts(response?.districts || (Array.isArray(response) ? response : []));
    } catch { setStoryDistricts([]); }
    finally { setStoryDistrictLoading(false); }
  };

  // ✅ SIMPLE IMAGE UPLOAD (No Crop)
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      message.error("Only JPG, PNG, WEBP allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("Max 5MB allowed");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleAdd = () => {
    setEditingStory(null);
    setImageFile(null);
    setImagePreview(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingStory(record);
    setImageFile(null);
    setImagePreview(record.image || null);

    form.setFieldsValue({
      name: record.title || record._raw?.name || "",
      husband_name: record.groom_name || record._raw?.husband_name || "",
      wife_name: record.bride_name || record._raw?.wife_name || "",
      text: record.story || record._raw?.text || "",
      marriage_date: record.marriage_date ? dayjs(record.marriage_date) : null,
      city: record.city || record._raw?.city || "",
      state: record.state || record._raw?.state || undefined,
      district: record.district || record._raw?.district || undefined,
    });

    if (record.state || record._raw?.state) handleStoryStateChange(record.state || record._raw?.state);
    setModalOpen(true);
  };

  const handleView = (record) => { setSelectedStory(record); setDetailsDrawerOpen(true); };
  const handleShare = (record) => { setShareStory(record); setShareModalOpen(true); };

  const handleToggleStatus = async (record) => {
    try {
      const newStatus = !record.is_active;
      const formData = new FormData();
      formData.append("is_active", newStatus);
      await User_SuccessStoriesUpdate(record.id, formData);
      message.success(`Story ${newStatus ? "activated" : "deactivated"}`);
      fetchStories();
    } catch { message.error("Failed to update status"); }
  };

  const handleToggleFeatured = async (record) => {
    try {
      const newFeatured = !record.is_featured;
      const formData = new FormData();
      formData.append("is_featured", newFeatured);
      await User_SuccessStoriesUpdate(record.id, formData);
      message.success(newFeatured ? "⭐ Marked as Featured!" : "Removed from Featured");
      fetchStories();
    } catch { message.error("Failed to update"); }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Success Story?", content: "This story will be permanently deleted.", okText: "Delete", okType: "danger", cancelText: "Cancel",
      onOk: async () => {
        try { await User_SuccessStoriesRemove(id); message.success("Deleted successfully"); fetchStories(); }
        catch { message.error("Failed to delete"); }
      },
    });
  };

  const handleSubmit = async (values) => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const formData = new FormData();
      if (!values.name?.trim() || !values.text?.trim()) return message.error("Please fill required fields");
      formData.append("name", values.name.trim());
      formData.append("text", values.text.trim());
      if (values.husband_name) formData.append("husband_name", values.husband_name.trim());
      if (values.wife_name) formData.append("wife_name", values.wife_name.trim());
      if (values.marriage_date) formData.append("marriage_date", values.marriage_date.format("YYYY-MM-DD"));
      if (values.state) formData.append("state", values.state);
      if (values.district) formData.append("district", values.district);
      if (values.city) formData.append("city", values.city);

      if (imageFile) formData.append("img", imageFile);
      else if (!editingStory) { message.error("Please upload image"); setSubmitting(false); return; }

      let res = editingStory ? await User_SuccessStoriesUpdate(editingStory.id, formData) : await User_SuccessStoriesPost(formData);
      if (res && (res.id || res.name || res.success)) {
        message.success(editingStory ? "✅ Updated!" : "✅ Added!");
        setModalOpen(false); form.resetFields(); setImageFile(null); setImagePreview(null); setEditingStory(null); await fetchStories();
      } else message.error("Failed to save");
    } catch (e) { message.error("Error saving story"); }
    finally { setSubmitting(false); }
  };

  const handleApproveStory = async (id) => {
    setApprovalLoading(prev => ({ ...prev, [id]: 'approve' }));
    try { await updateStoryStatus(id, 'approved'); message.success('✅ Approved!'); fetchStories(); }
    catch { message.error('Failed to approve'); }
    finally { setApprovalLoading(prev => ({ ...prev, [id]: null })); }
  };

  const handleOpenReject = (record) => { setRejectingStory(record); setRejectNote(''); setRejectModalOpen(true); };

  const handleConfirmReject = async () => {
    if (!rejectingStory) return;
    setApprovalLoading(prev => ({ ...prev, [rejectingStory.id]: 'reject' }));
    try {
      await updateStoryStatus(rejectingStory.id, 'rejected', rejectNote);
      message.success('Story rejected'); setRejectModalOpen(false); setRejectingStory(null); fetchStories();
    } catch { message.error('Failed to reject'); }
    finally { setApprovalLoading(prev => ({ ...prev, [rejectingStory?.id]: null })); }
  };

  const stats = {
    total: stories.length,
    active: stories.filter(s => s.is_active !== false).length,
    inactive: stories.filter(s => s.is_active === false).length,
    featured: stories.filter(s => s.is_featured === true).length,
    pending: stories.filter(s => s.status === 'pending').length,
    approved: stories.filter(s => s.status === 'approved').length,
    rejected: stories.filter(s => s.status === 'rejected').length,
  };

  // ✅ Submitted by badge config
  const getSubmitterBadge = (role) => {
    const config = {
      admin: { bg: '#ede9fe', color: '#7c3aed', border: '#c4b5fd', icon: <CrownOutlined />, label: 'Admin' },
      superadmin: { bg: '#ede9fe', color: '#7c3aed', border: '#c4b5fd', icon: <CrownOutlined />, label: 'Super Admin' },
      staff: { bg: '#dbeafe', color: '#2563eb', border: '#93c5fd', icon: <TeamOutlined />, label: 'Staff' },
      user: { bg: '#fff0f6', color: '#ff0055', border: '#ffb3c6', icon: <UserOutlined />, label: 'User' },
    };
    return config[role?.toLowerCase()] || config.user;
  };

  const exportColumns = [
    { key: "story_ref", label: "Story Ref", format: (r) => generateRefNumber("story", r.id || 0) },
    { key: "title", label: "Title", format: (r) => r.title || "N/A" },
    { key: "couple", label: "Couple", format: (r) => `${r.bride_name || "N/A"} & ${r.groom_name || "N/A"}` },
    { key: "submitted_by", label: "Submitted By", format: (r) => `${r.submitted_by_role || 'admin'} (${r.submitted_by_name || 'N/A'})` },
    { key: "status", label: "Status", format: (r) => r.status || "N/A" },
  ];

  const handleExportCSV = (all = false) => exportToCSV(all ? stories : filteredStories, exportColumns, "success_stories");
  const handleExportPDF = (all = false) => exportToPDF(all ? stories : filteredStories, exportColumns, { title: "Success Stories", filename: "success_stories" });

  const columns = [
    { title: "#", key: "sno", width: 45, fixed: "left", render: (_, __, i) => <span className="sno-cell">{i + 1}</span> },
    { title: "Ref", key: "story_ref", width: 100, render: (_, r) => <span className="ref-cell">{generateRefNumber("story", r.id)}</span> },
    {
      title: "Image", dataIndex: "image", key: "image", width: 80,
      render: (img) => img ? <Image src={img} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 8 }} preview={{ mask: <EyeOutlined /> }} fallback="https://via.placeholder.com/48x48" /> : <div className="no-image-cell"><PictureOutlined /></div>,
    },
    {
      title: "Title", dataIndex: "title", key: "title", width: 160,
      render: (t, r) => <div className="title-cell-wrapper">{r.is_featured && <TrophyOutlined className="featured-icon-small" />}<span className="title-cell">{t || "Untitled Story"}</span></div>,
    },
    {
      title: "Couple", key: "couple", width: 180,
      render: (_, r) => <div className="couple-cell"><HeartFilled className="couple-icon" /><div className="couple-names"><strong>{r.bride_name}</strong><span className="and-symbol"> & </span><strong>{r.groom_name}</strong></div></div>,
    },
    {
      title: "Submitted By", key: "submitted_by", width: 140,
      render: (_, r) => {
        const badge = getSubmitterBadge(r.submitted_by_role);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Tag style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`, fontWeight: 700, fontSize: 10, margin: 0, borderRadius: 10 }}>
              {badge.icon} {badge.label}
            </Tag>
            {r.submitted_by_name && (
              <span style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>{r.submitted_by_name}</span>
            )}
          </div>
        );
      }
    },
    { title: "Marriage Date", key: "marriage_date", width: 120, render: (_, r) => <span className="date-cell">{r.marriage_date ? formatDate(r.marriage_date) : "N/A"}</span> },
    {
      title: "Approval", key: "approval", width: 160,
      render: (_, record) => {
        if (record.status === 'approved') return <Tag color="green" icon={<CheckCircleOutlined />}>Approved</Tag>;
        if (record.status === 'rejected') return <Tooltip title={record.admin_note || 'No reason'}><Tag color="red" icon={<CloseCircleOutlined />}>Rejected</Tag></Tooltip>;
        return (
          <Space size={4}>
            <Button size="small" style={{ background: '#4caf50', border: 'none', color: '#fff', fontSize: 11, height: 26 }} icon={<CheckCircleOutlined />} loading={approvalLoading[record.id] === 'approve'} onClick={() => handleApproveStory(record.id)}>Approve</Button>
            <Button size="small" danger style={{ fontSize: 11, height: 26 }} icon={<CloseCircleOutlined />} loading={approvalLoading[record.id] === 'reject'} onClick={() => handleOpenReject(record)}>Reject</Button>
          </Space>
        );
      }
    },
    {
      title: "Actions", key: "action", width: 180, fixed: "right",
      render: (_, record) => (
        <Space size={3}>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} className="action-btn view" onClick={() => handleView(record)} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} className="action-btn edit" onClick={() => handleEdit(record)} /></Tooltip>
          <Tooltip title={record.is_featured ? "Remove Featured" : "Mark Featured"}><Button size="small" icon={<TrophyOutlined />} className={`action-btn ${record.is_featured ? "featured-active" : "featured"}`} onClick={() => handleToggleFeatured(record)} /></Tooltip>
          <Tooltip title="Share"><Button size="small" icon={<ShareAltOutlined />} className="action-btn share" onClick={() => handleShare(record)} /></Tooltip>
          <Tooltip title={record.is_active !== false ? "Deactivate" : "Activate"}><Button size="small" icon={record.is_active !== false ? <CloseCircleOutlined /> : <CheckCircleOutlined />} className={`action-btn ${record.is_active !== false ? "deactivate" : "activate"}`} onClick={() => handleToggleStatus(record)} /></Tooltip>
          <Tooltip title="Delete"><Button size="small" icon={<DeleteOutlined />} className="action-btn delete" onClick={() => handleDelete(record.id)} /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="success-stories-page">
      {/* HEADER */}
      <div className="header-stats-wrapper">
        <div className="page-header">
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><HeartFilled /></div>
              <div className="title-content">
                <h2 className="page-title">Success Stories</h2>
                <p className="page-subtitle">{filteredStories.length} of {stories.length} love stories</p>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <Button type="primary" className="add-story-btn icon-mobile-btn" icon={<PlusOutlined />} onClick={handleAdd}>
              <span className="hide-on-mobile">Add Story</span>
            </Button>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchStories} loading={loading} className="icon-btn icon-mobile-btn" /></Tooltip>
            <Dropdown menu={{ items: [{ key: "1", label: `Export Filtered`, onClick: () => handleExportCSV(false) }, { key: "2", label: `Export All`, onClick: () => handleExportCSV(true) }] }}>
              <Button icon={<FileExcelOutlined />} className="csv-btn icon-mobile-btn"><span className="hide-on-mobile">CSV <DownOutlined style={{ fontSize: 10 }} /></span></Button>
            </Dropdown>
            <Dropdown menu={{ items: [{ key: "1", label: `Export Filtered`, onClick: () => handleExportPDF(false) }, { key: "2", label: `Export All`, onClick: () => handleExportPDF(true) }] }}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn icon-mobile-btn"><span className="hide-on-mobile">PDF <DownOutlined style={{ fontSize: 10 }} /></span></Button>
            </Dropdown>
          </div>
        </div>

        <div className="mini-stats-bar">
          <div className="mini-stat total"><HeartFilled /> <b>{stats.total}</b> <span>Total</span></div><div className="mini-divider" />
          <div className="mini-stat" style={{ color: '#f57c00' }}>⏳ <b>{stats.pending}</b> <span>Pending</span></div><div className="mini-divider" />
          <div className="mini-stat" style={{ color: '#2e7d32' }}><CheckCircleOutlined /> <b>{stats.approved}</b> <span>Approved</span></div><div className="mini-divider" />
          <div className="mini-stat active"><CheckCircleOutlined /> <b>{stats.active}</b> <span>Active</span></div><div className="mini-divider" />
          <div className="mini-stat featured"><TrophyOutlined /> <b>{stats.featured}</b> <span>Featured</span></div>
        </div>
      </div>

      {/* FILTERS TOOLBAR */}
      <div className="quick-filters-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${statusFilter === "all" && !featuredFilter && approvalFilter === 'all' ? "active" : ""}`} onClick={() => { setStatusFilter("all"); setFeaturedFilter(false); setApprovalFilter("all"); }}><HeartFilled /> All</button>
          <button className={`filter-tab ${approvalFilter === 'pending' ? "active" : ""}`} style={approvalFilter !== 'pending' ? { color: '#f57c00' } : {}} onClick={() => { setApprovalFilter('pending'); setStatusFilter('all'); setFeaturedFilter(false); }}>⏳ Pending</button>
          <button className={`filter-tab ${approvalFilter === 'approved' ? "active" : ""}`} onClick={() => { setApprovalFilter('approved'); setStatusFilter('all'); setFeaturedFilter(false); }}><CheckCircleOutlined /> Approved</button>
          <button className={`filter-tab ${approvalFilter === 'rejected' ? "active" : ""}`} onClick={() => { setApprovalFilter('rejected'); setStatusFilter('all'); setFeaturedFilter(false); }}><CloseCircleOutlined /> Rejected</button>
          <button className={`filter-tab featured ${featuredFilter ? "active" : ""}`} onClick={() => setFeaturedFilter(!featuredFilter)}><TrophyOutlined /> Featured</button>
        </div>
        <div className="toolbar-right">
          <Input placeholder="Search name, ref..." prefix={<SearchOutlined />} value={searchText} onChange={(e) => setSearchText(e.target.value)} className="toolbar-search pill-search-box" allowClear />
          <div className="view-toggle hide-on-mobile">
            <button className={`view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")}><AppstoreOutlined /> Grid</button>
            <button className={`view-btn ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")}><UnorderedListOutlined /> List</button>
          </div>
        </div>
      </div>

      {/* VIEW RENDERING */}
      <div className="data-container">
        {isMobile ? (
          <div className="mobile-native-list">
            {filteredStories.map((story) => {
              const submitterBadge = getSubmitterBadge(story.submitted_by_role);
              return (
                <div key={story.id} className="mobile-story-card">
                  <div className="msc-header">
                    <span className="msc-ref">{generateRefNumber("story", story.id)}</span>
                    <div className="msc-badges">
                      {story.is_featured && <Tag color="gold" icon={<TrophyOutlined />}>Featured</Tag>}
                      {story.status === 'approved' ? <Tag color="green">Approved</Tag> : story.status === 'rejected' ? <Tag color="red">Rejected</Tag> : <Tag color="orange">Pending</Tag>}
                    </div>
                  </div>

                  <div className="msc-body" onClick={() => handleView(story)}>
                    <div className="msc-img-box">
                      {story.image ? <img src={story.image} alt="Couple" /> : <div className="msc-placeholder"><HeartFilled /></div>}
                    </div>
                    <div className="msc-info">
                      <h4>{story.bride_name || "Wife"} <span style={{ color: '#ff80aa' }}>&</span> {story.groom_name || "Husband"}</h4>
                      {story.title && <p className="msc-title">"{story.title}"</p>}
                      <Tag style={{ background: submitterBadge.bg, color: submitterBadge.color, border: `1px solid ${submitterBadge.border}`, fontWeight: 700, fontSize: 9, marginTop: 4, borderRadius: 8 }}>
                        {submitterBadge.icon} {submitterBadge.label}
                      </Tag>
                      <p className="msc-date">{formatDate(story.created_date)}</p>
                    </div>
                  </div>

                  <div className="msc-actions-scroll">
                    {story.status !== 'approved' && (
                      <Button size="small" className="msc-action approve" icon={<CheckCircleOutlined />} loading={approvalLoading[story.id] === 'approve'} onClick={() => handleApproveStory(story.id)} />
                    )}
                    {story.status !== 'rejected' && (
                      <Button size="small" className="msc-action reject" icon={<CloseCircleOutlined />} loading={approvalLoading[story.id] === 'reject'} onClick={() => handleOpenReject(story)} />
                    )}
                    <div className="msc-divider-v" />
                    <Button size="small" className="msc-action view" icon={<EyeOutlined />} onClick={() => handleView(story)} />
                    <Button size="small" className="msc-action edit" icon={<EditOutlined />} onClick={() => handleEdit(story)} />
                    <Button size="small" className={`msc-action ${story.is_featured ? 'featured-active' : 'feature'}`} icon={<TrophyOutlined />} onClick={() => handleToggleFeatured(story)} />
                    <Button size="small" className={`msc-action ${story.is_active !== false ? 'deactivate' : 'activate'}`} icon={story.is_active !== false ? <CloseCircleOutlined /> : <CheckCircleOutlined />} onClick={() => handleToggleStatus(story)} />
                    <Button size="small" className="msc-action delete" icon={<DeleteOutlined />} onClick={() => handleDelete(story.id)} />
                  </div>
                </div>
              );
            })}
            {filteredStories.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No stories found</div>}
          </div>
        ) : (
          <>
            {viewMode === "grid" && (
              <div className="stories-grid-view">
                {filteredStories.map((story) => {
                  const submitterBadge = getSubmitterBadge(story.submitted_by_role);
                  return (
                    <div key={story.id} className={`story-premium-card ${story.is_active === false ? "inactive-card" : ""}`}>
                      {story.is_featured && <div className="featured-badge"><TrophyOutlined /> Featured</div>}
                      <div className="premium-img-wrapper" onClick={() => handleView(story)}>
                        {story.image ? <img src={story.image} alt="Couple" className="premium-story-img" /> : <div className="premium-img-placeholder"><HeartFilled /></div>}
                        <div className="img-overlay"><div className="overlay-content"><EyeOutlined /> View Story</div></div>
                      </div>
                      <div className="premium-story-content">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                          <Tag style={{ background: submitterBadge.bg, color: submitterBadge.color, border: `1px solid ${submitterBadge.border}`, fontWeight: 700, fontSize: 10, margin: 0, borderRadius: 10 }}>
                            {submitterBadge.icon} {submitterBadge.label}
                          </Tag>
                          {story.status === 'approved' && <Tag color="green" style={{ fontSize: 10, borderRadius: 10 }}>✓ Approved</Tag>}
                          {story.status === 'pending' && <Tag color="orange" style={{ fontSize: 10, borderRadius: 10 }}>⏳ Pending</Tag>}
                          {story.status === 'rejected' && <Tag color="red" style={{ fontSize: 10, borderRadius: 10 }}>✗ Rejected</Tag>}
                        </div>
                        <h4 className="premium-couple-name"><HeartFilled className="heart-icon" />{story.bride_name} <span className="and-symbol">&</span> {story.groom_name}</h4>
                        {story.title && <p className="premium-story-title">"{story.title}"</p>}
                        <p className="premium-story-desc">{story.story ? story.story.slice(0, 80) + "..." : "No description"}</p>
                        <div className="premium-card-actions">
                          <Button size="small" icon={<EyeOutlined />} className="premium-btn view" onClick={() => handleView(story)}>View</Button>
                          <Button size="small" icon={<EditOutlined />} className="premium-btn edit" onClick={() => handleEdit(story)}>Edit</Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {viewMode === "list" && (
              <div className="table-card">
                <Table rowKey="id" columns={columns} dataSource={filteredStories} loading={loading} size="small" pagination={{ pageSize: 10 }} scroll={{ x: 1500 }} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Reject Modal */}
      <Modal title="Reject Story" open={rejectModalOpen} onOk={handleConfirmReject} onCancel={() => setRejectModalOpen(false)} okText="Confirm Reject" okButtonProps={{ danger: true, loading: approvalLoading[rejectingStory?.id] === 'reject' }}>
        <p>Why are you rejecting this story?</p>
        <Input.TextArea rows={4} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Enter reason (optional)" />
      </Modal>

      {/* ✅ PREMIUM ADD/EDIT MODAL */}
      <Modal
        title={null}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setImageFile(null); setImagePreview(null); setEditingStory(null); }}
        footer={null}
        width={isMobile ? "100%" : 720}
        style={{ top: isMobile ? 0 : 30 }}
        className="premium-story-modal"
        closable={false}
      >
        {/* Modal Custom Header */}
        <div className="pm-header">
          <div className="pm-header-left">
            <div className="pm-header-icon">
              {editingStory ? <EditOutlined /> : <HeartFilled />}
            </div>
            <div>
              <h3 className="pm-header-title">
                {editingStory ? 'Edit Success Story' : 'Add New Success Story'}
              </h3>
              <p className="pm-header-subtitle">
                {editingStory ? 'Update the couple details below' : 'Share a beautiful love story'}
              </p>
            </div>
          </div>
          <button
            className="pm-close-btn"
            onClick={() => { setModalOpen(false); form.resetFields(); setImageFile(null); setImagePreview(null); setEditingStory(null); }}
          >
            <CloseCircleOutlined />
          </button>
        </div>

        <div className="pm-body">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>

            {/* IMAGE UPLOAD - Simple Direct */}
            <div className="pm-image-section">
              <label className="pm-section-label">
                <CameraOutlined /> Couple Photo {!editingStory && <span style={{ color: '#ff0055' }}>*</span>}
              </label>

              {imagePreview ? (
                <div className="pm-img-preview-wrap">
                  <img src={imagePreview} alt="Preview" />
                  <button type="button" className="pm-img-remove-btn" onClick={removeImage}>
                    <CloseCircleOutlined />
                  </button>
                </div>
              ) : (
                <label className="pm-img-upload-box">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <PictureOutlined style={{ fontSize: 40, color: '#ff0055', marginBottom: 10 }} />
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#ff0055', marginBottom: 4 }}>
                    Click to Upload Photo
                  </div>
                  <div style={{ fontSize: 11, color: '#999' }}>
                    JPG, PNG, WEBP • Max 5MB
                  </div>
                </label>
              )}

              {imagePreview && (
                <label className="pm-img-change-label">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <span className="pm-img-change-btn">
                    <PictureOutlined /> Change Photo
                  </span>
                </label>
              )}
            </div>

            {/* Story Title */}
            <div className="pm-section">
              <Form.Item
                name="name"
                label={<span className="pm-label"><FileTextOutlined /> Story Title</span>}
                rules={[{ required: true, message: 'Story title required' }]}
              >
                <Input
                  placeholder="e.g., A Beautiful Journey of Love"
                  size="large"
                  className="pm-input"
                />
              </Form.Item>
            </div>

            {/* Couple Names */}
            <div className="pm-section">
              <div className="pm-section-title">
                <HeartFilled style={{ color: '#ff0055' }} /> Couple Information
              </div>
              <Row gutter={12}>
                <Col span={isMobile ? 24 : 12}>
                  <Form.Item
                    name="husband_name"
                    label={<span className="pm-label"><UserOutlined /> Husband's Name</span>}
                    rules={[{ required: true, message: 'Husband name required' }]}
                  >
                    <Input placeholder="Husband's full name" size="large" className="pm-input" prefix={<UserOutlined style={{ color: '#3b82f6' }} />} />
                  </Form.Item>
                </Col>
                <Col span={isMobile ? 24 : 12}>
                  <Form.Item
                    name="wife_name"
                    label={<span className="pm-label"><UserOutlined /> Wife's Name</span>}
                    rules={[{ required: true, message: 'Wife name required' }]}
                  >
                    <Input placeholder="Wife's full name" size="large" className="pm-input" prefix={<UserOutlined style={{ color: '#ff0055' }} />} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Marriage Date & Location */}
            <div className="pm-section">
              <div className="pm-section-title">
                <EnvironmentOutlined style={{ color: '#10b981' }} /> Date & Location
              </div>
              <Row gutter={12}>
                <Col span={isMobile ? 24 : 12}>
                  <Form.Item
                    name="marriage_date"
                    label={<span className="pm-label"><CalendarOutlined /> Marriage Date</span>}
                  >
                    <DatePicker size="large" style={{ width: '100%' }} className="pm-input" format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={isMobile ? 24 : 12}>
                  <Form.Item
                    name="state"
                    label={<span className="pm-label"><EnvironmentOutlined /> State</span>}
                  >
                    <Select
                      placeholder="Select State"
                      size="large"
                      showSearch
                      allowClear
                      loading={storyStateLoading}
                      onChange={handleStoryStateChange}
                      filterOption={(input, option) => option.children?.toLowerCase().includes(input.toLowerCase())}
                    >
                      {storyStates.map((s) => (
                        <Option key={s.state || s.name || s} value={s.state || s.name || s}>{s.state || s.name || s}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={isMobile ? 24 : 12}>
                  <Form.Item
                    name="district"
                    label={<span className="pm-label"><EnvironmentOutlined /> District</span>}
                  >
                    <Select
                      placeholder="Select District"
                      size="large"
                      showSearch
                      allowClear
                      loading={storyDistrictLoading}
                      disabled={storyDistricts.length === 0}
                    >
                      {storyDistricts.map((d) => (
                        <Option key={d.district || d.name || d} value={d.district || d.name || d}>{d.district || d.name || d}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={isMobile ? 24 : 12}>
                  <Form.Item
                    name="city"
                    label={<span className="pm-label"><EnvironmentOutlined /> City</span>}
                  >
                    <Input placeholder="City name" size="large" className="pm-input" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Story Text */}
            <div className="pm-section">
              <Form.Item
                name="text"
                label={<span className="pm-label"><FileTextOutlined /> Their Love Story</span>}
                rules={[{ required: true, message: 'Story text required' }]}
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Share their beautiful journey — how they met, fell in love, and got married..."
                  className="pm-textarea"
                  showCount
                  maxLength={2000}
                />
              </Form.Item>
            </div>

            {/* Actions */}
            <div className="pm-actions">
              <Button
                size="large"
                onClick={() => { setModalOpen(false); form.resetFields(); setImageFile(null); setImagePreview(null); setEditingStory(null); }}
                className="pm-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                size="large"
                className="pm-submit-btn"
                icon={editingStory ? <EditOutlined /> : <PlusOutlined />}
              >
                {editingStory ? 'Update Story' : 'Publish Story'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* ✅ PREMIUM DETAILS DRAWER */}
      <Drawer
        title={null}
        placement="right"
        width={isMobile ? "100%" : 520}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
        className="premium-story-drawer"
        closable={false}
      >
        {selectedStory && (() => {
          const submitterBadge = getSubmitterBadge(selectedStory.submitted_by_role);
          return (
            <>
              {/* Custom Drawer Header */}
              <div className="psd-header">
                <div className="psd-header-icon"><HeartFilled /></div>
                <div style={{ flex: 1 }}>
                  <h3 className="psd-header-title">Story Details</h3>
                  <p className="psd-header-subtitle">Complete story information</p>
                </div>
                <button className="psd-close-btn" onClick={() => setDetailsDrawerOpen(false)}>
                  <CloseCircleOutlined />
                </button>
              </div>

              <div className="psd-body">
                {/* HERO IMAGE */}
                <div className="psd-hero-img">
                  {selectedStory.image ? (
                    <img src={selectedStory.image} alt="couple" />
                  ) : (
                    <div className="psd-hero-placeholder"><HeartFilled /></div>
                  )}
                  {selectedStory.is_featured && (
                    <div className="psd-featured-badge">
                      <TrophyOutlined /> Featured Story
                    </div>
                  )}
                </div>

                {/* COUPLE NAMES */}
                <div className="psd-names-block">
                  <div className="psd-heart-decor">💕</div>
                  <h2 className="psd-names">
                    {selectedStory.bride_name || "Wife"} <span className="psd-and">&</span> {selectedStory.groom_name || "Husband"}
                  </h2>
                  {selectedStory.title && (
                    <p className="psd-title">"{selectedStory.title}"</p>
                  )}
                </div>

                {/* STATUS BADGES */}
                <div className="psd-badges">
                  <Tag className={`psd-status-tag status-${selectedStory.status}`}>
                    {selectedStory.status === 'approved' && <><CheckCircleOutlined /> Approved</>}
                    {selectedStory.status === 'pending' && <>⏳ Pending Review</>}
                    {selectedStory.status === 'rejected' && <><CloseCircleOutlined /> Rejected</>}
                  </Tag>
                  <Tag className={`psd-active-tag ${selectedStory.is_active ? 'active' : 'inactive'}`}>
                    {selectedStory.is_active ? '● Active' : '○ Inactive'}
                  </Tag>
                </div>

                {/* SUBMITTED BY CARD */}
                <div className="psd-submitter-card">
                  <div className="psd-submitter-icon" style={{ background: submitterBadge.bg, color: submitterBadge.color }}>
                    {submitterBadge.icon}
                  </div>
                  <div className="psd-submitter-info">
                    <div className="psd-submitter-label">SUBMITTED BY</div>
                    <div className="psd-submitter-name">
                      <span style={{ color: submitterBadge.color, fontWeight: 700 }}>{submitterBadge.label}</span>
                      {selectedStory.submitted_by_name && (
                        <span style={{ color: '#666', marginLeft: 8 }}>• {selectedStory.submitted_by_name}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* INFO GRID */}
                <div className="psd-info-grid">
                  <div className="psd-info-item">
                    <div className="psd-info-icon"><IdcardOutlined /></div>
                    <div className="psd-info-content">
                      <div className="psd-info-label">Reference ID</div>
                      <div className="psd-info-value mono">{generateRefNumber("story", selectedStory.id)}</div>
                    </div>
                  </div>

                  {selectedStory.marriage_date && (
                    <div className="psd-info-item">
                      <div className="psd-info-icon"><CalendarOutlined /></div>
                      <div className="psd-info-content">
                        <div className="psd-info-label">Marriage Date</div>
                        <div className="psd-info-value">{formatDate(selectedStory.marriage_date)}</div>
                      </div>
                    </div>
                  )}

                  {(selectedStory.city || selectedStory.district || selectedStory.state) && (
                    <div className="psd-info-item full">
                      <div className="psd-info-icon"><EnvironmentOutlined /></div>
                      <div className="psd-info-content">
                        <div className="psd-info-label">Location</div>
                        <div className="psd-info-value">
                          {[selectedStory.city, selectedStory.district, selectedStory.state].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="psd-info-item">
                    <div className="psd-info-icon"><CalendarOutlined /></div>
                    <div className="psd-info-content">
                      <div className="psd-info-label">Added On</div>
                      <div className="psd-info-value">{formatDate(selectedStory.created_date)}</div>
                    </div>
                  </div>
                </div>

                {/* STORY TEXT */}
                <div className="psd-story-section">
                  <div className="psd-section-header">
                    <FileTextOutlined /> Their Love Story
                  </div>
                  <div className="psd-story-text">
                    {selectedStory.story || "No story description provided"}
                  </div>
                </div>

                {/* ADMIN NOTE (if rejected) */}
                {selectedStory.status === 'rejected' && selectedStory.admin_note && (
                  <div className="psd-reject-note">
                    <div className="psd-section-header" style={{ color: '#dc2626' }}>
                      <CloseCircleOutlined /> Rejection Reason
                    </div>
                    <p>{selectedStory.admin_note}</p>
                  </div>
                )}

                {/* ACTION BUTTONS */}
                <div className="psd-actions">
                  <Button
                    className="psd-action-btn edit"
                    icon={<EditOutlined />}
                    onClick={() => { setDetailsDrawerOpen(false); handleEdit(selectedStory); }}
                  >
                    Edit Story
                  </Button>
                  <Button
                    className={`psd-action-btn ${selectedStory.is_featured ? 'unfeature' : 'feature'}`}
                    icon={<TrophyOutlined />}
                    onClick={() => handleToggleFeatured(selectedStory)}
                  >
                    {selectedStory.is_featured ? 'Remove Featured' : 'Mark Featured'}
                  </Button>
                  <Button
                    className={`psd-action-btn ${selectedStory.is_active ? 'deactivate' : 'activate'}`}
                    icon={selectedStory.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                    onClick={() => handleToggleStatus(selectedStory)}
                  >
                    {selectedStory.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    danger
                    className="psd-action-btn delete"
                    icon={<DeleteOutlined />}
                    onClick={() => { setDetailsDrawerOpen(false); handleDelete(selectedStory.id); }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </>
          );
        })()}
      </Drawer>
    </div>
  );
}

export default SuccessStories;